import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import { getJwtToken } from './auth';
import { normalizeCertificateDetail } from '../utils/arcWeldingVerificationUi';

const BASE = '/arc-welding';

const unwrap = (response) => get(response, 'data.data', get(response, 'data', response));

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

// —— Form options (all dropdowns) ——

export const fetchArcWeldingFormOptions = async () => {
  const res = await axiosConfig.get(`${BASE}/form-options`);
  return unwrap(res);
};

// —— Sites (legacy; prefer form-options.sites) ——
export const fetchArcWeldingSites = async () => {
  const res = await axiosConfig.get(`${BASE}/sites`);
  return unwrap(res);
};

// —— Equipment profiles ——

export const fetchEquipmentProfiles = async (params = {}) => {
  const res = await axiosConfig.get(`${BASE}/equipment-profiles${buildQuery(params)}`);
  return unwrap(res);
};

export const fetchEquipmentProfile = async (id) => {
  const res = await axiosConfig.get(`${BASE}/equipment-profiles/${id}`);
  return unwrap(res);
};

export const createEquipmentProfile = async (body) => {
  const res = await axiosConfig.post(`${BASE}/equipment-profiles`, body);
  return unwrap(res);
};

export const updateEquipmentProfile = async (id, body) => {
  const res = await axiosConfig.put(`${BASE}/equipment-profiles/${id}`, body);
  return unwrap(res);
};

export const retireEquipmentProfile = async (id) => {
  const res = await axiosConfig.delete(`${BASE}/equipment-profiles/${id}`);
  return unwrap(res);
};

const assertXlsxBlob = async (response, fallbackMessage) => {
  if (!response.ok) {
    let detail = fallbackMessage;
    try {
      const errBody = await response.clone().json();
      detail = errBody?.message || errBody?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const contentType = (response.headers.get('content-type') || blob.type || '').toLowerCase();
  const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  const isZip = header[0] === 0x50 && header[1] === 0x4b; // PK..
  const looksJson =
    contentType.includes('application/json') || contentType.includes('text/') || header[0] === 0x7b; /* { */
  if (!isZip || looksJson) {
    throw new Error(
      'Export did not return a valid Excel file (check BE Content-Type and xlsx bytes).'
    );
  }
  return blob;
};

export const exportEquipmentProfilesXlsx = async () => {
  const authToken = await getJwtToken();
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}${BASE}/equipment-profiles-export/xlsx`, {
    headers: { Authorization: authToken },
  });
  return assertXlsxBlob(response, 'Failed to export equipment profiles');
};

export const importEquipmentProfilesXlsx = async (fileBase64) => {
  const res = await axiosConfig.post(`${BASE}/equipment-profiles-import/xlsx`, { fileBase64 });
  return unwrap(res);
};

// —— Verification criteria ——

export const fetchVerificationCriteria = async (params = {}) => {
  const res = await axiosConfig.get(`${BASE}/verification-criteria${buildQuery(params)}`);
  return unwrap(res);
};

export const fetchVerificationCriterion = async (id) => {
  const res = await axiosConfig.get(`${BASE}/verification-criteria/${id}`);
  return unwrap(res);
};

export const createVerificationCriterion = async (body) => {
  const res = await axiosConfig.post(`${BASE}/verification-criteria`, body);
  return unwrap(res);
};

export const updateVerificationCriterion = async (id, body) => {
  const res = await axiosConfig.put(`${BASE}/verification-criteria/${id}`, body);
  return unwrap(res);
};

export const deleteVerificationCriterion = async (id) => {
  const res = await axiosConfig.delete(`${BASE}/verification-criteria/${id}`);
  return unwrap(res);
};

export const exportVerificationCriteriaXlsx = async () => {
  const authToken = await getJwtToken();
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}${BASE}/verification-criteria-export/xlsx`, {
    headers: { Authorization: authToken },
  });
  return assertXlsxBlob(response, 'Failed to export verification criteria');
};

export const importVerificationCriteriaXlsx = async (fileBase64) => {
  const res = await axiosConfig.post(`${BASE}/verification-criteria-import/xlsx`, { fileBase64 });
  return unwrap(res);
};

// —— Certificates ——

export const fetchCertificates = async (params = {}) => {
  const res = await axiosConfig.get(`${BASE}/certificates${buildQuery(params)}`);
  return unwrap(res);
};

export const fetchCertificatesForCombine = async (params = {}) => {
  const res = await axiosConfig.get(`${BASE}/certificates/select-for-combine${buildQuery(params)}`);
  return unwrap(res);
};

export const fetchCertificate = async (id) => {
  const res = await axiosConfig.get(`${BASE}/certificates/${id}`);
  return normalizeCertificateDetail(unwrap(res));
};

export const createCertificate = async (body) => {
  const res = await axiosConfig.post(`${BASE}/certificates`, body);
  return normalizeCertificateDetail(unwrap(res));
};

export const updateCertificate = async (id, body) => {
  const res = await axiosConfig.put(`${BASE}/certificates/${id}`, body);
  return normalizeCertificateDetail(unwrap(res));
};

export const saveCertificateTestPoints = async (id, body) => {
  const res = await axiosConfig.post(`${BASE}/certificates/${id}/test-points`, body);
  return unwrap(res);
};

export const submitCertificateForReview = async (id, body) => {
  const res = await axiosConfig.post(`${BASE}/certificates/${id}/submit-for-review`, body ?? {});
  return normalizeCertificateDetail(unwrap(res));
};

export const authoriseCertificate = async (id, body) => {
  const res = await axiosConfig.post(`${BASE}/certificates/${id}/authorise`, body);
  return normalizeCertificateDetail(unwrap(res));
};

export const fetchCertificatePdf = async (id) => {
  const res = await axiosConfig.get(`${BASE}/certificates/${id}/pdf`);
  return unwrap(res);
};

const normalizeEmailList = (emails) => {
  if (!emails) return [];
  const list = Array.isArray(emails) ? emails : String(emails).split(',');
  return list.map((email) => String(email).trim()).filter(Boolean);
};

/** Regenerate PDF, upload to S3, and email presigned download link (Issued certificates only). */
export const shareCertificate = async (id, { emailTo, emailToDestinations, emails } = {}) => {
  const emailList = normalizeEmailList(emailTo ?? emailToDestinations ?? emails);
  const res = await axiosConfig.post(`${BASE}/certificates/${id}/share`, {
    emailTo: emailList,
  });
  return unwrap(res);
};

export const combineCertificates = async (body) => {
  const res = await axiosConfig.post(`${BASE}/certificates/combine`, body);
  return unwrap(res);
};

export const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
